import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GridApiPro, GridFilterModel } from '@mui/x-data-grid-pro';
import { DATA_GRID_EVENTS } from 'components/common/dataGrid/constants';
import { isActiveFilterItem } from 'components/common/dataGrid/filters/activity';
import { DataGridInstanceName } from 'services/storage/keys';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { areFilterModelsEqual, getDeselectedFilterModel, getEffectiveFilters } from '../filterModelUtils';
import { FilterPresetListItem, GetDefaultFilterItemFn, SavedFilterPreset } from '../types';

type UseFilterPresetsParams = {
  apiRef: MutableRefObject<GridApiPro>;
  dataGridInstanceName?: DataGridInstanceName;
  staticPresets?: SavedFilterPreset[];
  excludeFieldFromCount?: string;
  getDefaultFilterItem?: GetDefaultFilterItemFn;
};

export type UseFilterPresetsResult = {
  isDialogOpen: boolean;
  isRenameMode: boolean;
  dialogPresetName: string;
  presets: FilterPresetListItem[];
  activePresetId: string;
  activePreset: FilterPresetListItem | undefined;
  isModified: boolean;
  hasUnsavedFilters: boolean;
  setDialogPresetName: (value: string) => void;
  openCreateDialog: () => void;
  openRenameDialog: (presetId: string) => void;
  closeDialog: () => void;
  submitDialog: () => void;
  selectPreset: (presetId: string) => void;
  deletePreset: (presetId: string) => void;
  saveChangesToPreset: () => void;
  discardChanges: () => void;
  clearFilters: () => void;
  revertToCommittedFilters: () => void;
};

const createEmptyFilterModel = (): GridFilterModel => ({ items: [] });

function loadUserPresets(
  dataGridInstanceName?: DataGridInstanceName
): SavedFilterPreset[] {
  if (!dataGridInstanceName) return [];

  return LocalStorageManager.getDataGridFilterPresets<SavedFilterPreset[]>(
    dataGridInstanceName,
    []
  );
}

function loadActivePresetId(
  dataGridInstanceName?: DataGridInstanceName
): string {
  if (!dataGridInstanceName) return '';
  return LocalStorageManager.getDataGridSelectedPresetId(dataGridInstanceName);
}

function saveActivePresetId(
  dataGridInstanceName: DataGridInstanceName | undefined,
  presetId: string
): void {
  if (!dataGridInstanceName) return;
  LocalStorageManager.setDataGridSelectedPresetId(dataGridInstanceName, presetId);
}

export function useFilterPresets({
  apiRef,
  dataGridInstanceName,
  staticPresets = [],
  excludeFieldFromCount,
  getDefaultFilterItem,
}: UseFilterPresetsParams): UseFilterPresetsResult {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogPresetName, setDialogPresetName] = useState('');
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [currentFilterModel, setCurrentFilterModel] = useState<GridFilterModel>(
    createEmptyFilterModel
  );
  const [isFilterModelReady, setIsFilterModelReady] = useState(false);
  const [activePresetId, setActivePresetIdState] = useState<string>(() =>
    loadActivePresetId(dataGridInstanceName)
  );
  const lastAppliedModelRef = useRef<GridFilterModel | null>(null);
  const committedFilterModelRef = useRef<GridFilterModel | null>(null);
  const initialLoadDoneRef = useRef(false);

  const [presets, setPresets] = useState<SavedFilterPreset[]>(() =>
    loadUserPresets(dataGridInstanceName)
  );

  const setActivePresetId = useCallback(
    (presetId: string) => {
      setActivePresetIdState(presetId);
      saveActivePresetId(dataGridInstanceName, presetId);
    },
    [dataGridInstanceName]
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const setupSubscription = (): void => {
      if (!apiRef?.current?.subscribeEvent) {
        timeoutId = setTimeout(setupSubscription, 100);
        return;
      }

      const initialModel =
        apiRef.current.state?.filter?.filterModel ?? createEmptyFilterModel();
      setCurrentFilterModel(initialModel);
      setIsFilterModelReady(true);

      unsubscribe = apiRef.current.subscribeEvent(
        DATA_GRID_EVENTS.FILTER_MODEL_CHANGE,
        (model) => {
          setCurrentFilterModel(model as GridFilterModel);
          setIsFilterModelReady(true);
        }
      );
    };

    setupSubscription();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      unsubscribe?.();
    };
  }, [apiRef]);

  const savePresets = useCallback(
    (nextPresets: SavedFilterPreset[]) => {
      if (!dataGridInstanceName) return;

      LocalStorageManager.setDataGridFilterPresets(dataGridInstanceName, nextPresets);
      setPresets(nextPresets);
    },
    [dataGridInstanceName]
  );

  const updatePreset = useCallback(
    (presetId: string, update: Partial<SavedFilterPreset>) => {
      const nextPresets = presets.map((preset) =>
        preset.id === presetId ? { ...preset, ...update } : preset
      );
      savePresets(nextPresets);
    },
    [presets, savePresets]
  );

  const getCurrentFilterModel = useCallback((): GridFilterModel => {
    const cloned = structuredClone(currentFilterModel);
    cloned.items = (cloned.items ?? []).filter(isActiveFilterItem);
    return cloned;
  }, [currentFilterModel]);

  const allPresets = useMemo<FilterPresetListItem[]>(() => {
    return [
      ...staticPresets.map((preset) => ({ ...preset, isStatic: true })),
      ...presets.map((preset) => ({ ...preset, isStatic: false })),
    ];
  }, [presets, staticPresets]);

  const activePreset = useMemo(
    () => allPresets.find((p) => p.id === activePresetId),
    [allPresets, activePresetId]
  );

  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    if (!apiRef?.current?.setFilterModel) return;
    if (!activePresetId) return;

    const preset = allPresets.find((p) => p.id === activePresetId);
    if (!preset) {
      setActivePresetId('');
      return;
    }

    initialLoadDoneRef.current = true;

    if (excludeFieldFromCount) {
      const currentModel = apiRef.current.state?.filter?.filterModel;
      const fixedFilter = currentModel?.items?.find(
        (item) => item.field === excludeFieldFromCount
      );

      const presetItemsWithoutFixed = (preset.model.items ?? []).filter(
        (item) => item.field !== excludeFieldFromCount
      );

      const mergedModel: GridFilterModel = {
        ...preset.model,
        items: fixedFilter
          ? [fixedFilter, ...presetItemsWithoutFixed]
          : presetItemsWithoutFixed,
      };

      apiRef.current.setFilterModel(mergedModel);
      setCurrentFilterModel(mergedModel);
      lastAppliedModelRef.current = mergedModel;
    } else {
      apiRef.current.setFilterModel(preset.model);
      setCurrentFilterModel(preset.model);
      lastAppliedModelRef.current = preset.model;
    }
  }, [apiRef, activePresetId, allPresets, excludeFieldFromCount, setActivePresetId]);

  const isModified = useMemo(() => {
    if (!isFilterModelReady) return false;
    if (!activePresetId || !activePreset) return false;
    return !areFilterModelsEqual(
      currentFilterModel,
      activePreset.model,
      excludeFieldFromCount
    );
  }, [
    activePresetId,
    activePreset,
    currentFilterModel,
    excludeFieldFromCount,
    isFilterModelReady,
  ]);

  const hasUnsavedFilters = useMemo(() => {
    if (!isFilterModelReady) return false;
    if (activePresetId) return false;
    const effectiveItems = getEffectiveFilters(
      currentFilterModel.items ?? [],
      excludeFieldFromCount
    );
    return effectiveItems.length > 0;
  }, [
    activePresetId,
    currentFilterModel,
    excludeFieldFromCount,
    isFilterModelReady,
  ]);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogPresetName('');
    setEditingPresetId(null);
  }, []);

  const openCreateDialog = useCallback(() => {
    setEditingPresetId(null);
    setDialogPresetName('');
    setIsDialogOpen(true);
  }, []);

  const openRenameDialog = useCallback(
    (presetId: string) => {
      const preset = [...staticPresets, ...presets].find((p) => p.id === presetId);
      if (!preset) return;

      setEditingPresetId(presetId);
      setDialogPresetName(preset.name);
      setIsDialogOpen(true);
    },
    [presets, staticPresets]
  );

  const applyFilterModel = useCallback(
    (model: GridFilterModel) => {
      if (excludeFieldFromCount) {
        const currentModel = apiRef.current.state?.filter?.filterModel;
        const fixedFilter = currentModel?.items?.find(
          (item) => item.field === excludeFieldFromCount
        );

        const modelItemsWithoutFixed = (model.items ?? []).filter(
          (item) => item.field !== excludeFieldFromCount
        );

        const mergedModel: GridFilterModel = {
          ...model,
          items: fixedFilter
            ? [fixedFilter, ...modelItemsWithoutFixed]
            : modelItemsWithoutFixed,
        };

        apiRef.current.setFilterModel(mergedModel);
        lastAppliedModelRef.current = mergedModel;
      } else {
        apiRef.current.setFilterModel(model);
        lastAppliedModelRef.current = model;
      }
    },
    [apiRef, excludeFieldFromCount]
  );

  const selectPreset = useCallback(
    (presetId: string) => {
      if (presetId === activePresetId) {
        setActivePresetId('');
        const deselectedModel = getDeselectedFilterModel(
          apiRef,
          excludeFieldFromCount,
          getDefaultFilterItem
        );
        apiRef.current.setFilterModel(deselectedModel);
        committedFilterModelRef.current = deselectedModel;
        return;
      }

      const preset = allPresets.find((item) => item.id === presetId);
      if (!preset) return;

      setActivePresetId(presetId);
      applyFilterModel(preset.model);
      committedFilterModelRef.current = preset.model;
    },
    [activePresetId, allPresets, applyFilterModel, setActivePresetId, apiRef, excludeFieldFromCount, getDefaultFilterItem]
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      const currentIndex = allPresets.findIndex((p) => p.id === presetId);
      const nextPresets = presets.filter((preset) => preset.id !== presetId);
      savePresets(nextPresets);

      if (activePresetId === presetId) {
        const nextAllPresets = allPresets.filter((p) => p.id !== presetId);
        const previousIndex = Math.max(0, currentIndex - 1);
        const previousPreset = nextAllPresets[previousIndex];

        if (previousPreset) {
          setActivePresetId(previousPreset.id);
          applyFilterModel(previousPreset.model);
        } else {
          setActivePresetId('');
        }
      }
    },
    [presets, savePresets, activePresetId, allPresets, applyFilterModel, setActivePresetId]
  );

  const clearFilters = useCallback(() => {
    setActivePresetId('');
    const clearedModel = getDeselectedFilterModel(
      apiRef,
      excludeFieldFromCount,
      getDefaultFilterItem
    );
    apiRef.current.setFilterModel(clearedModel);
    committedFilterModelRef.current = clearedModel;
  }, [apiRef, excludeFieldFromCount, getDefaultFilterItem, setActivePresetId]);

  const saveChangesToPreset = useCallback(() => {
    if (!activePresetId || !activePreset) return;
    if (activePreset.isStatic) return;

    const updatedModel = getCurrentFilterModel();
    updatePreset(activePresetId, { model: updatedModel });
    lastAppliedModelRef.current = updatedModel;
    committedFilterModelRef.current = updatedModel;
  }, [activePresetId, activePreset, getCurrentFilterModel, updatePreset]);

  const discardChanges = useCallback(() => {
    if (!activePresetId || !activePreset) return;
    applyFilterModel(activePreset.model);
    committedFilterModelRef.current = activePreset.model;
  }, [activePresetId, activePreset, applyFilterModel]);

  const submitDialog = useCallback(() => {
    const trimmedName = dialogPresetName.trim();
    if (!trimmedName) return;

    if (editingPresetId) {
      updatePreset(editingPresetId, { name: trimmedName });
    } else {
      if (!dataGridInstanceName) return;

      const newPresetId = crypto.randomUUID();
      const newModel = getCurrentFilterModel();
      const nextPreset: SavedFilterPreset = {
        id: newPresetId,
        name: trimmedName,
        model: newModel,
      };
      const nextPresets = [nextPreset, ...presets];

      savePresets(nextPresets);
      setActivePresetId(newPresetId);
      committedFilterModelRef.current = newModel;
    }

    closeDialog();
  }, [
    closeDialog,
    dataGridInstanceName,
    dialogPresetName,
    editingPresetId,
    getCurrentFilterModel,
    presets,
    savePresets,
    setActivePresetId,
    updatePreset,
  ]);

  const revertToCommittedFilters = useCallback(() => {
    if (committedFilterModelRef.current) {
      apiRef.current.setFilterModel(committedFilterModelRef.current);
    } else {
      const clearedModel = getDeselectedFilterModel(
        apiRef,
        excludeFieldFromCount,
        getDefaultFilterItem
      );
      apiRef.current.setFilterModel(clearedModel);
    }
    if (activePreset) {
      setActivePresetId(activePreset.id);
    } else {
      setActivePresetId('');
    }
  }, [apiRef, activePreset, excludeFieldFromCount, getDefaultFilterItem, setActivePresetId]);

  return {
    isDialogOpen,
    isRenameMode: editingPresetId !== null,
    dialogPresetName,
    presets: allPresets,
    activePresetId,
    activePreset,
    isModified,
    hasUnsavedFilters,
    setDialogPresetName,
    openCreateDialog,
    openRenameDialog,
    closeDialog,
    submitDialog,
    selectPreset,
    deletePreset,
    saveChangesToPreset,
    discardChanges,
    clearFilters,
    revertToCommittedFilters,
  };
}
